import React, {useCallback, useEffect} from 'react';
import {reduxForm, InjectedFormProps, SubmissionError, Field} from 'redux-form';
import {Linking, Platform, View} from 'react-native';
import {useAppDispatch} from '../../../../../redux';
import {useSelector} from 'react-redux';
import {PasswordFields} from '../../../../login/registratonOrRecovery/passwordFields';
import {Button} from '../../../../../shared/components/button';
import {BaseField} from '../../../../../shared/components/baseField';
import {Forms as LoginForms} from '../../../../../shared/models/forms';
import {createNewPassword, getNewCode, checkCode} from '../../../../../redux/actions/login';
import {profileSelector} from '../../../../../redux/selectors/profile';
import {RequestedOperationType, SendCodeChannel, SendCodeChannelType} from '../../../../../shared/models/api';
import {onlyDigitsPipe} from '../../../../../shared/pipes';
import {ServerValidationError} from '../../../../../shared/models/serverValidationError';
import {Text} from '../../../../../shared/components/text';
import {
  getFormErrorsForPasswordCreation,
  PasswordCreationFormData,
  PasswordCreationFormProps,
  PasswordCreationStep,
} from './model';
import {CountDownTimer, CountDownTimerRefProps} from '../../../../login/components/countDownTimer';
import {addNotification} from '../../../../../redux/actions/snackbar';
import {SnackbarNotificationType} from '../../../../../shared/components/snackbar/model';
import {useSafeNavigation} from '../../../../../shared/hooks/useSaveNavigation';
import {useFocusEffect} from '@react-navigation/native';

const QUERY_PARAM_CODE_REGEX = /[?&]code=([^&#]+)/;
const TRAILING_DIGITS_REGEX = /\/(\d+)(?:\?.*)?$/;
const MAX_CODEREQUEST_TRIES = 3;

const PasswordCreationForm_: React.FC<InjectedFormProps<PasswordCreationFormData> & PasswordCreationFormProps> = ({
  handleSubmit,
  onSubmit,
  initialCodeProps,
  invalid,
  submitting,
  pristine,
  error: formError,
}) => {
  const dispatch = useAppDispatch();
  const profile = useSelector(profileSelector());
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [step, setStep] = React.useState<PasswordCreationStep>(PasswordCreationStep.RequestCode);
  const [selectedChannel, setSelectedChannel] = React.useState<SendCodeChannelType>(SendCodeChannel.Email);
  const [isTimerActive, setIsTimerActive] = React.useState(false);
  const timerRef = React.useRef<CountDownTimerRefProps>(null);
  const TIMER_DURATION = 60; // 60 seconds
  const nav = useSafeNavigation();
  const lastProcessedCodeRef = React.useRef<string | null>(null);
  const [initialCode, setInitialCode] = React.useState(initialCodeProps);
  const [triesCount, setTriesCount] = React.useState(MAX_CODEREQUEST_TRIES);

  useEffect(() => {
    if (initialCodeProps && step === PasswordCreationStep.RequestCode && Platform.OS === 'web') {
      const autoVerifyCode = async () => {
        try {
          setIsSubmitting(true);
          const identifier = profile?.holder?.mobilePhone || '';
          await dispatch(checkCode(initialCodeProps, identifier, RequestedOperationType.SetPasswordConfirmation));
          setStep(PasswordCreationStep.SetPassword);
        } catch (error) {
          console.error('Auto code verification failed:', error);
          dispatch(
            addNotification({
              message: 'Ошибка при проверке кода',
              type: SnackbarNotificationType.Error,
            }),
          );
          setStep(PasswordCreationStep.RequestCode);
        } finally {
          setIsSubmitting(false);
        }
      };
      autoVerifyCode();
    }
  }, [initialCodeProps, step, dispatch]);

  const extractRecoveryCodeFromUrl = (rawUrl: string): string | null => {
    try {
      const codeParamMatch = rawUrl.match(QUERY_PARAM_CODE_REGEX);
      if (codeParamMatch && codeParamMatch[1]) {
        return decodeURIComponent(codeParamMatch[1]);
      }

      const trailingDigitsMatch = rawUrl.match(TRAILING_DIGITS_REGEX);
      return trailingDigitsMatch?.[1] ?? null;
    } catch {
      return null;
    }
  };

  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web') return;

      let mounted = true;

      const onUrl = async ({url}: {url: string}) => {
        if (!mounted || !url) return;
        if (step !== PasswordCreationStep.EmailSent) return;
        const code = extractRecoveryCodeFromUrl(url);
        if (!code) return;
        if (lastProcessedCodeRef.current === code) return;
        lastProcessedCodeRef.current = code;

        try {
          setInitialCode(code);
          setIsSubmitting(true);
          const identifier = profile?.holder?.mobilePhone || '';
          await dispatch(checkCode(code, identifier, RequestedOperationType.SetPasswordConfirmation));
          if (!mounted) return;
          setStep(PasswordCreationStep.SetPassword);
          setIsSubmitting(false);
        } catch (error) {
          if (__DEV__) console.error('Auto code verification failed:', error);
          dispatch(
            addNotification({
              message: 'Ошибка при проверке кода',
              type: SnackbarNotificationType.Error,
            }),
          );
        }
      };

      const subscription = Linking.addEventListener('url', onUrl);
      return () => {
        mounted = false;
        subscription.remove();
      };
    }, [step, dispatch, profile?.holder?.mobilePhone]),
  );

  const handleFormSubmission = async (formData: PasswordCreationFormData, channelOverride?: SendCodeChannelType) => {
    const submissionError = getFormErrorsForPasswordCreation(formData, step);
    if (submissionError) {
      console.log('submissionError', submissionError);
      throw new SubmissionError(submissionError);
    }

    try {
      setIsSubmitting(true);
      const currentChannel = channelOverride || selectedChannel;
      if (step === PasswordCreationStep.RequestCode) {
        await handleCodeRequest(currentChannel);
      } else if (step === PasswordCreationStep.EnterCode) {
        const identifier = profile?.holder?.mobilePhone || '';
        await dispatch(checkCode(formData.smsCode || '', identifier, RequestedOperationType.SetPasswordConfirmation));
        setStep(PasswordCreationStep.SetPassword);
      } else if (step === PasswordCreationStep.SetPassword) {
        await dispatch(createNewPassword(formData.smsCode || initialCode || '', formData.password || '', false));
        dispatch(
          addNotification({
            message: 'Пароль успешно добавлен',
            type: SnackbarNotificationType.Success,
          }),
        );
        if (onSubmit) {
          onSubmit();
        } else if (nav) {
          nav.goBack();
        }
      }
    } catch (error) {
      if (__DEV__) console.error('Password creation step failed:', error);
      setTriesCount((prev) => --prev);
      const serverError = error as ServerValidationError;
      const errorText = serverError.data?.[0]?.message || serverError.message || 'Произошла ошибка. Попробуйте еще раз';

      if (step === PasswordCreationStep.EnterCode) {
        throw new SubmissionError({
          smsCode: errorText,
          _error: errorText,
        });
      } else {
        throw new SubmissionError({
          _error: errorText,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCodeRequest = async (channelType: SendCodeChannelType) => {
    const identifier = profile?.holder?.usc || profile?.holder?.mobilePhone;
    if (!identifier) {
      throw new SubmissionError({
        _error: 'Не удалось получить данные профиля для отправки кода',
      });
    }

    try {
      await dispatch(getNewCode(onlyDigitsPipe(identifier), false, channelType, 'create'));

      setIsTimerActive(true);
      timerRef.current?.resetTimer();

      if (channelType === SendCodeChannel.Email) {
        setStep(PasswordCreationStep.EmailSent);
      } else {
        setStep(PasswordCreationStep.EnterCode);
      }
    } catch (error) {
      const serverError = error as ServerValidationError;
      const errorText = serverError.data?.[0]?.message || serverError.message || 'Ошибка отправки кода';
      throw new SubmissionError({
        error: errorText,
      });
    }
  };

  const handleSmsRequest = async () => {
    setSelectedChannel(SendCodeChannel.Sms);
    await handleFormSubmission({} as PasswordCreationFormData, SendCodeChannel.Sms);
  };

  const handleEmailRequest = async () => {
    setSelectedChannel(SendCodeChannel.Email);
    await handleFormSubmission({} as PasswordCreationFormData, SendCodeChannel.Email);
  };

  const handleRefreshCode = async () => {
    if (isTimerActive) return;

    try {
      setIsSubmitting(true);
      const identifier = profile?.holder?.usc || profile?.holder?.mobilePhone;
      if (identifier) {
        await dispatch(getNewCode(onlyDigitsPipe(identifier), true, selectedChannel));
        setIsTimerActive(true);
        timerRef.current?.resetTimer();
      }
    } catch (error) {
      if (__DEV__) console.error('Failed to refresh code:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTimerEnd = () => {
    setIsTimerActive(false);
    setTriesCount(MAX_CODEREQUEST_TRIES);
  };

  const renderStepContent = () => {
    switch (step) {
      case PasswordCreationStep.RequestCode:
        return (
          <View style={{padding: 20}}>
            <Text style={{textAlign: 'center', fontSize: 20}}>Выберите способ получения кода подтверждения</Text>
            <View style={{marginBottom: 15}}>
              <Button
                id='request-password-code-sms'
                style={{alignSelf: 'center', width: '100%', marginTop: 20}}
                type='primary'
                text='Отправить код по SMS'
                disabled={isSubmitting}
                onPress={handleSmsRequest}
              />
            </View>
            {profile?.holder?.email && (
              <View style={{marginBottom: 20}}>
                <Button
                  id='request-password-code-email'
                  style={{alignSelf: 'center', width: '100%'}}
                  type='secondary'
                  text='Отправить код на email'
                  disabled={isSubmitting}
                  onPress={handleEmailRequest}
                />
              </View>
            )}
          </View>
        );

      case PasswordCreationStep.EmailSent:
        return (
          <View style={{padding: 20, minWidth: Platform.OS === 'web' ? 400 : '100%'}}>
            <Text style={{marginBottom: 20, textAlign: 'center', fontSize: 20}}>
              Код подтверждения отправлен на вашу электронную почту. Проверьте почту и следуйте инструкциям в письме.
            </Text>
            <View style={{marginBottom: 10}}>
              <CountDownTimer
                ref={timerRef}
                until={TIMER_DURATION}
                onRefresh={isTimerActive ? undefined : handleRefreshCode}
                onTimerEnd={handleTimerEnd}
              />
            </View>
          </View>
        );

      case PasswordCreationStep.EnterCode:
        return (
          <View style={{padding: 20, minWidth: Platform.OS === 'web' ? 400 : '100%'}}>
            <Text style={{marginBottom: 20, textAlign: 'center', fontSize: 20}}>
              {selectedChannel === SendCodeChannel.Sms ? 'Введите код из SMS' : 'Введите код из email'}
            </Text>
            <View style={{marginBottom: 20}}>
              <Field
                style={{width: '100%'}}
                component={BaseField}
                label='Код подтверждения из 6 цифр'
                type='text'
                name='smsCode'
                normalize={(value: string) => {
                  if (!value) return '';
                  const digitsOnly = value.replace(/\D/g, '');
                  return digitsOnly.slice(0, 6);
                }}
                onSubmitEditing={() => handleSubmit((values) => handleFormSubmission(values))()}
              />
              {formError && <Text style={{color: 'red', marginBottom: 10, maxWidth: 400}}>{formError}</Text>}
            </View>
            <View style={{marginBottom: 10}}>
              <Button
                id='verify-password-code'
                type='primary'
                style={{alignSelf: 'center', width: '100%'}}
                text='Подтвердить код'
                disabled={isSubmitting || triesCount === 0}
                onPress={() => handleSubmit((values) => handleFormSubmission(values))()}
              />
            </View>
            <View style={{marginBottom: 10}}>
              <Text style={{color: triesCount === 0 ? 'red' : 'black', maxWidth: 400, textAlign: 'center'}}>
                {triesCount !== 0
                  ? `Осталось попыток ввода проверочного кода: ${triesCount}`
                  : 'Превышено максимальное количество попыток ввода проверочного кода. Запросите новый код.'}
              </Text>
              <CountDownTimer
                ref={timerRef}
                until={TIMER_DURATION}
                onRefresh={isTimerActive ? undefined : handleRefreshCode}
                onTimerEnd={handleTimerEnd}
              />
            </View>
          </View>
        );

      case PasswordCreationStep.SetPassword:
        return (
          <View style={{padding: 20}}>
            <PasswordFields onSubmit={() => handleSubmit((values) => handleFormSubmission(values))()} />
            <Button
              id='create-password-submit'
              style={{alignSelf: 'center', width: '100%', marginTop: 20}}
              type='primary'
              text='Создать пароль'
              disabled={isSubmitting || pristine || invalid || submitting}
              onPress={() => handleSubmit((values) => handleFormSubmission(values))()}
            />
          </View>
        );

      default:
        return null;
    }
  };

  return renderStepContent();
};

const validate = (values: PasswordCreationFormData) => {
  const errors = getFormErrorsForPasswordCreation(values, PasswordCreationStep.SetPassword);
  return errors;
};

export const PasswordCreationForm = reduxForm<PasswordCreationFormData, PasswordCreationFormProps>({
  form: LoginForms.passwordCreationForm,
  validate,
})(PasswordCreationForm_);
